import * as React from "react";
import {autorun, runInAction} from "mobx";
import {observer} from "mobx-react";

import "./search.scss";

import {
    SearchConfiguration,
    CustomSearchSourceConfiguration,
    LayerSearchSourceConfiguration,
    LocatorSearchSourceConfiguration,
    SearchSourceType,
    SearchSourceQueryConfiguration,
    CoordinateSearchSourceConfiguration, SearchSourceConfiguration
} from "../../config/search-configuration";

import EsriSearchViewModel from "@arcgis/core/widgets/Search/SearchViewModel";
import EsriCollection from "@arcgis/core/core/Collection";
import {Input, Menu, Label} from "semantic-ui-react";
import Autosuggest, {
    ChangeEvent,
    RenderSuggestionParams,
    SuggestionSelectedEventData,
    SuggestionsFetchRequestedParams
} from "react-autosuggest";
import AutosuggestHighlightMatch from "autosuggest-highlight/match";
import AutosuggestHighlightParse from "autosuggest-highlight/parse";
import {SearchStation} from "../../../search/search-station";
import {PathUtils} from "../../../utils/path-utils";
import _ from "lodash";
import SearchSource from "@arcgis/core/widgets/Search/SearchSource";
import "../../../utils/evaluate";
import EsriGraphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {
    FillSymbolConfiguration,
    PictureMarkerSymbolConfiguration,
    SimpleLineSymbolConfiguration
} from "../../config/symbol-configuration";
// https://github.com/Esri/arcgis-webpack-plugin/issues/88
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as projection from "@arcgis/core/geometry/projection";
import MatomoTracker from "@datapunt/matomo-tracker-js";
import {Icon} from "../icon/icon";
import * as arcade from "@arcgis/core/arcade";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import {FormatUtils} from "../../../utils/format-utils";
import {ExtentConfiguration, PointConfiguration} from "../../config/geometry-configuration";
import Extent from "@arcgis/core/geometry/Extent";
import {MapUtils} from "../../../utils/map-utils";
import {IAppStore, withStore} from "../../stores/abstract-app-store";
import SearchViewModel from "@arcgis/core/widgets/Search/SearchViewModel";

export interface SearchProps {
    config: SearchConfiguration;  // widgetsSearch is also the type for configuration object
    onLoad?: (searchViewModel: EsriSearchViewModel) => any;
    tracker?: MatomoTracker | undefined;
    keyAppend?: string | undefined;
    store?: IAppStore; // this is injected by withStore
}
interface featureResult {
    feature: __esri.Graphic;
    sourceIndex: number;
}
interface ExtendedSearchViewModelEventHandler extends __esri.SearchViewModelSearchCompleteEventHandler {
    feature?: __esri.Graphic;
}
export class ExtendedSearchViewModel extends SearchViewModel {
    constructor(properties?: __esri.SearchViewModelProperties) {
        super(properties);
    }
    on(name: "suggestion-selected" | "result-found" | "result-detail-found",
        eventHandler: ExtendedSearchViewModelEventHandler): IHandle;
    // TODO: Variante ohne den Code von interface.d.ts class SearchViewModel zu duplizieren
    on(name: "search-clear", eventHandler: __esri.SearchViewModelSearchClearEventHandler): IHandle;
    on(name: "search-start", eventHandler: __esri.SearchViewModelSearchStartEventHandler): IHandle;
    on(name: "suggest-start", eventHandler: __esri.SearchViewModelSuggestStartEventHandler): IHandle;
    on(name: "search-complete", eventHandler: __esri.SearchViewModelSearchCompleteEventHandler): IHandle;
    on(name: "select-result", eventHandler: __esri.SearchViewModelSelectResultEventHandler): IHandle;
    // @ts-expect-error TODO: Lösung für Error TS2391 finden
    on(name: "suggest-complete", eventHandler: __esri.SearchViewModelSuggestCompleteEventHandler): IHandle;
}

// When suggestion is clicked, Autosuggest needs to populate the input
// based on the clicked suggestion. Teach Autosuggest how to calculate the
// input value for every given suggestion.
// Result is of type ESRI SuggestResult
// https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchViewModel.html#SuggestResult
const getSuggestionValue = (suggestion: __esri.SuggestResult) => suggestion.text;

// React 18: getSectionSuggestions is called with undefined after selecting suggestion on mobile device
// https://github.com/moroshko/react-autosuggest/issues/853
const getSectionSuggestions = (section: any | undefined) => section?.suggestions? section.suggestions: [];


@observer
export class SearchBase extends React.Component<SearchProps, any> {
    autorunDisposers: any[] = [];
    popupEnabled: number[] = [];
    autoNavigate: number[] = [];
    suggestions: any[] = [];
    suggestionKeyMap: Map<number, Map<string, string[]>> = new Map();
    widgetKey = "search";
    config: SearchConfiguration;

    constructor(props: SearchProps) {
        super(props);
        this.config = _.cloneDeep(props.config);
        // Autosuggest is a controlled component.
        // This means that you need to provide an input value
        // and an onChange handler that updates this value (see below).
        // Suggestions also need to be provided to the Autosuggest,
        // and they are initially empty because the Autosuggest is closed.
        this.state = {
            isLoading: true,
            layersLoaded: 0,
            placeholder: "",
            viewModel: null,
            value: "",
            searchTerm: "",
            suggestions: [],
            sectionShowAll: Array(this.config.sources.length).fill(false)
        };
        if (props.keyAppend) {
            this.widgetKey += "-" + props.keyAppend;
        }
        this.handleShowAll = this.handleShowAll.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
        this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
        this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
        this.renderSectionTitle = this.renderSectionTitle.bind(this);
        this.renderSuggestion = this.renderSuggestion.bind(this);
        this.clearInputField = this.clearInputField.bind(this);
    }

    onChange(event: React.FormEvent, changeEvent: ChangeEvent): void {
        this.setState({
            value: changeEvent.newValue
        });
    }

    // Autosuggest will call this function every time you need to update suggestions.
    onSuggestionsFetchRequested(params: SuggestionsFetchRequestedParams): void {
        // get suggestions asynchronously
        this.getSuggestions(params.value);
    }

    // Autosuggest will call this function every time you need to clear suggestions.
    onSuggestionsClearRequested(): void {
        const {sectionShowAll} = this.state;
        sectionShowAll.fill(false);
        this.setState({
            sectionShowAll,
            suggestions: []
        });
    }

    // called when suggestion is selected by mouse or keyboard
    onSuggestionSelected(event: React.FormEvent<any>, data: SuggestionSelectedEventData<any>): void {
        const {viewModel, searchTerm, suggestions} = this.state;
        if (!viewModel) {
            // search not fully loaded yet
            return;
        }
        const {tracker, store} = this.props;
        if (tracker) {
            tracker.trackSiteSearch({
                keyword: searchTerm,
                count: suggestions.reduce((a: number, s: any)=> a + s.numResults, 0)});
        }
        const profile: __esri.Profile = {
            variables: [{
                name: "$feature",
                type: "feature"
            }]
        };
        // quick & dirty fix für EGID Suche
        if (data.sectionIndex && this.config.sources[data.sectionIndex].sourceType === SearchSourceType.Locator) {
            const sConfig = this.config.sources[data.sectionIndex] as LocatorSearchSourceConfiguration;
            if (sConfig.categories && sConfig.categories.includes("EGID")){
                data.suggestion.text = data.suggestion.text.substring(0, data.suggestion.text.indexOf(" "));
            }
        }
        viewModel.emit("suggestion-selected", {data});
        viewModel.search(data.suggestion).then((searchResponse: __esri.SearchResponse) => {
            // add graphic to map with marker
            searchResponse.results.forEach((result: __esri.SearchViewModelSearchResponseResults) => {
                const feature = result.results[0].feature;
                const extent = result.results[0].extent;
                runInAction(()=> {
                    const sourceConfig = this.config.sources[result.sourceIndex];
                    viewModel.emit("result-found", {sourceConfig, feature});
                    if (!store?.PopupStore.disabled) {
                        store?.PopupStore.setVisible(false);
                    }
                    if (!sourceConfig.resultGraphicEnabled) {
                        return;
                    }
                    let queryConfigs: SearchSourceQueryConfiguration[] = [];
                    if (sourceConfig.queries && feature.attributes.id) {
                        const ids = feature.attributes.id.split(", ");
                        ids.forEach((id: string)=>{
                            // Version und Typ Postfix entfernen
                            const metaid = id.replace(/_v(\d+)(_PY|_PT|_LI|)(\d{0,1})$/i, "");
                            let queryConfig;
                            if (sourceConfig.query?.id === metaid) {
                                queryConfig = sourceConfig.query;
                            } else if(sourceConfig.queries) {
                                queryConfig = sourceConfig.queries.find((q) => q.id === metaid);
                            }
                            if (queryConfig) {
                                queryConfigs.push(queryConfig);
                            }
                        });
                    }
                    if (queryConfigs.length === 0 && sourceConfig.query) {
                        queryConfigs = [sourceConfig.query];
                    }
                    if (queryConfigs.length > 0) {
                        let firstFlag = true;
                        queryConfigs.forEach((queryConfig)=>{
                            let searchLayer: FeatureLayer | __esri.Sublayer | undefined;
                            if ("sourceLayer" in feature && (feature as any)?.sourceLayer){
                                searchLayer = (feature as any)?.sourceLayer;
                            } else if (sourceConfig.sourceType === SearchSourceType.Layer && queryConfig.id === "self"
                                && sourceConfig.hasOwnProperty("layer")) {
                                searchLayer =
                                    (sourceConfig as LayerSearchSourceConfiguration).layer as __esri.FeatureLayer;
                            } else if (queryConfig.portalItem){
                                let popupTemplate;
                                if (queryConfig.popupTemplate) {
                                    if (typeof queryConfig.popupTemplate === "string") {
                                        if (!store?.PopupStore.disabled) {
                                            // TODO: use from mapViewerConfig
                                            popupTemplate
                                                = store?.mapViewerConfig.getLayerTemplate(
                                                    queryConfig.popupTemplate)?.template;
                                        }
                                        if (!popupTemplate) {
                                            // eslint-disable-next-line no-console
                                            console.warn("Kein Template für "
                                                + queryConfig.id+(queryConfig.layerId ?" ("+queryConfig.layerId+")": "")
                                                + " mit Bezeichung " + queryConfig.popupTemplate
                                                + " gefunden");
                                        }
                                    } else {
                                        popupTemplate = queryConfig.popupTemplate;
                                    }
                                }
                                const layerConfig = {
                                    layerType: "feature",
                                    layerId: (queryConfig.layerId) ? queryConfig.layerId : undefined,
                                    portalItem: queryConfig.portalItem,
                                    opacity: 1,
                                    listMode: "hide",
                                    legendEnabled: false,
                                    popupTemplate,
                                    title: (queryConfig.title) ? queryConfig.title : undefined
                                };
                                searchLayer = new FeatureLayer(layerConfig as __esri.FeatureLayerProperties);
                            }
                            if (searchLayer) {
                                const arcadeExecutor = arcade.createArcadeExecutor(queryConfig.expression, profile);
                                arcadeExecutor.then((ae) => {
                                    let where = ae.execute({
                                        $feature: feature
                                    });
                                    if (typeof where === "string" && searchLayer) {
                                        // definitionExpression in Suche berücksichtigen, falls diese definiert ist.
                                        if (searchLayer.definitionExpression) {
                                            where = "(" + where + ") AND (" + searchLayer.definitionExpression + ")";
                                        }
                                        const query = searchLayer.createQuery();
                                        query.where = where;
                                        // outFields setzen, falls in der Konfiguration vorhanden
                                        // oder alle Felder falls Popup aktiviert
                                        if (queryConfig?.outFields?.length
                                            || (!store?.PopupStore.disabled
                                                && this.popupEnabled.includes(result.sourceIndex))) {
                                            query.outFields = (queryConfig?.outFields?.length)?
                                                queryConfig.outFields : ["*"];
                                        }
                                        searchLayer.queryFeatures(query)
                                            .then((response: __esri.FeatureSet) => {
                                                viewModel.emit("result-detail-found",
                                                    {sourceConfig, response});
                                                if(response.features.length === 0) {
                                                    // eslint-disable-next-line no-console
                                                    console.warn("Kein detail Resultat gefunden");
                                                    return;
                                                }
                                                if (this.config.resultSymbol
                                                    && this.autoNavigate.includes(result.sourceIndex)) {
                                                    if (firstFlag) {
                                                        store?.mapStore?.view?.graphics.removeAll();
                                                    }
                                                    if (!store?.PopupStore.disabled
                                                        && this.popupEnabled.includes(result.sourceIndex)) {
                                                        let popupPoint = feature.geometry as __esri.Point;
                                                        // check if point is within the result geometry
                                                        if (!geometryEngine.within(popupPoint,
                                                            response.features[0].geometry)) {
                                                            // set buffer and get a point inside  the result geometry
                                                            const buffer = geometryEngine.buffer(
                                                                response.features[0].geometry, -1, "meters" );
                                                            popupPoint = geometryEngine.nearestCoordinate(
                                                                buffer as __esri.Geometry, popupPoint).coordinate;
                                                        }
                                                        if (firstFlag) {
                                                            runInAction(()=>{
                                                                store?.PopupStore.setMapPoint(popupPoint);
                                                                store?.PopupStore.setFeatures(
                                                                    response.features);
                                                            });
                                                        } else {
                                                            store?.PopupStore.addFeatures(response.features);
                                                        }
                                                    } else {
                                                        let highlightSymbol:
                                                        FillSymbolConfiguration
                                                        | PictureMarkerSymbolConfiguration
                                                        | SimpleLineSymbolConfiguration;
                                                        if (response.features[0].geometry.type === "polygon"){
                                                            highlightSymbol = this.config.resultSymbol.highlight;
                                                        } else if (response.features[0].geometry.type === "polyline"){
                                                            highlightSymbol = this.config.resultSymbol.line;
                                                        } else {
                                                            highlightSymbol = this.config.resultSymbol.pointer;
                                                        }
                                                        const geometries: __esri.Geometry[] = [];
                                                        response.features.forEach((item) => {
                                                            geometries.push(item.geometry);
                                                        });
                                                        if (response.features[0].geometry.type === "polyline") {
                                                            store?.mapStore?.view?.graphics.push(
                                                                new EsriGraphic({
                                                                    geometry: geometryEngine.union(geometries),
                                                                    symbol: highlightSymbol
                                                                })
                                                            );
                                                        } else {
                                                            response.features.forEach((item) => {
                                                                store?.mapStore?.view?.graphics.push(
                                                                    new EsriGraphic({
                                                                        geometry: item.geometry,
                                                                        symbol: highlightSymbol
                                                                    })
                                                                );
                                                            });
                                                        }
                                                        if (response.features.length > 1
                                                            || this.autoNavigate.includes(result.sourceIndex)) {
                                                            viewModel.clear();
                                                            if(store?.mapStore?.view) {
                                                                store.mapStore.view.goTo(
                                                                    MapUtils.calculateMinExtent(geometries)
                                                                ).catch((error) => {
                                                                    if (error.name !== "view:goto-interrupted") {
                                                                        // eslint-disable-next-line no-console
                                                                        console.error(error);
                                                                    }
                                                                });
                                                            }
                                                        }
                                                    }
                                                    firstFlag = false;
                                                }
                                            });
                                    }
                                });
                            }
                        });
                    } else {
                        if (this.config.resultSymbol) {
                            const highlightSymbol = this.config.resultSymbol.pointer;
                            store?.mapStore?.view?.graphics.push(
                                new EsriGraphic({
                                    geometry: feature.geometry,
                                    symbol: highlightSymbol
                                })
                            );
                            if(store?.mapStore?.view) {
                                store.mapStore.view.goTo(MapUtils.calculateMinExtent(extent)).catch((error) => {
                                    if (error.name !== "view:goto-interrupted") {
                                        // eslint-disable-next-line no-console
                                        console.error(error);
                                    }
                                });
                            }
                        }
                    }
                });
                /* const symbol = result.source.resultSymbol;
                const feature = response.results[0].results[0].feature;
                feature.symbol = symbol;
                geometries.push(feature.geometry);
                view.add(feature);*/
            });
        });
    }

    /**
     * Register, that a layer has to be loaded
     */
    registerLayerLoading(): void {
        this.setState((state: any) => ({ layersLoaded: state.layersLoaded - 1 }));
    }

    /**
     * Mark a layer as loaded
     */
    layerLoaded(): void {
        this.setState((state: any) => ({ layersLoaded: state.layersLoaded + 1 }));
    }
    handleShowAll(e: React.MouseEvent, sourceIndex: number): void {
        const {sectionShowAll, suggestions} = this.state;
        const index = this.suggestions.findIndex((item) => item.sourceIndex === sourceIndex);
        // clone wird benötigt, da sonst Autosuggest die Resultate nicht neu rendert
        let newSuggestions = _.cloneDeep(suggestions);
        const oldValue = sectionShowAll[sourceIndex];
        sectionShowAll.fill(false);
        // falls vorher nicht alle Resultate gelistet werden, alle anzeigen
        if (!oldValue) {
            sectionShowAll[sourceIndex] = true;
            newSuggestions[index].suggestions = this.suggestions[index].suggestions;
        }
        // limitieren der Resultate
        newSuggestions = newSuggestions.map((section: any) => {
            const sourceConfig = this.config.sources[section.sourceIndex];
            if ((sourceIndex !== section.sourceIndex || oldValue)
                && sourceConfig.maxResults
                && section.suggestions.length > sourceConfig.maxResults) {
                section.suggestions.length = sourceConfig.maxResults;
            }
            return section;
        });
        this.setState({sectionShowAll, suggestions: newSuggestions});
        e.preventDefault();
    }
    renderSectionTitle(section: any): React.ReactElement | null {
        const {sectionShowAll} = this.state;
        // hide title if no suggestion for the section is available
        if (section.suggestions.length === 0) {
            return null;
        }
        const sourceConfig = this.config.sources[section.sourceIndex];
        let matches = section.numResults;
        let addClass = "";
        if (typeof sourceConfig.maxSuggestions === "number") {
            if(section.numResults >= sourceConfig.maxSuggestions) {
                matches = "> " + (section.numResults -1);
                addClass += " hasmore";
            }
        }
        if (typeof sourceConfig.maxResults !== "number") {
            sourceConfig.maxResults = 8;
        }
        if(section.numResults > sourceConfig.maxResults) {
            addClass += " handle-showall";
            if (sectionShowAll[section.sourceIndex]) {
                addClass += " showall";
            }
        }
        return (
            <span
                onClick={(event: React.MouseEvent) => {
                    this.handleShowAll(event, section.sourceIndex);
                }}
                className={addClass}>
                <Label horizontal>{matches}</Label> <span>{section.title}</span>
                {(section.numResults > sourceConfig.maxResults)? <>
                    <Icon name="navigate_plus" />
                    <Icon name="navigate_minus" />
                </>: ""}
            </span>
        );
    }
    renderSuggestion(suggestion: __esri.SuggestResult, params: RenderSuggestionParams): React.ReactElement {
        const suggestionText = suggestion.text;
        const matches = AutosuggestHighlightMatch(suggestionText, params.query);
        const parts = AutosuggestHighlightParse(suggestionText, matches);
        return (
            <span className={"suggestion-content " + PathUtils.slug(suggestion.text)}>
                <span className="name">
                    {
                        parts.map((part, index) => (
                            <span className={part.highlight ? "highlight" : undefined} key={index}>{part.text}</span>
                        ))
                    }
                </span>
            </span>
        );
    };
    clearInputField(): void {
        this.setState({value: ""});
    }
    render(): React.ReactElement {
        const { value, suggestions, isLoading, placeholder } = this.state;
        let searchField;
        if (isLoading) {
            // show disabled placeholder field
            searchField = <Input loading disabled placeholder={"Wird geladen..."} />;
        } else {
            // Autosuggest will pass through all these props to the input.
            const inputProps = {
                placeholder,
                value,
                onChange: this.onChange,
            };
            searchField = <div className={(value!=="")? "geo-input-has-value": "geo-input-has-no-value"}>
                <Autosuggest
                    suggestions={suggestions}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    getSuggestionValue={getSuggestionValue}
                    renderSuggestion={this.renderSuggestion}
                    highlightFirstSuggestion={true}
                    multiSection={true}
                    renderSectionTitle={this.renderSectionTitle}
                    getSectionSuggestions={getSectionSuggestions}
                    inputProps={inputProps}
                    onSuggestionSelected={this.onSuggestionSelected}
                    focusInputOnSuggestionClick={false}
                />
                <Icon name={"magnifying_glass"}/>
                <Icon
                    className={"geo-clear-field"}
                    name={"navigate_cross"}
                    onClick={this.clearInputField}
                    title={"Text löschen"} />
            </div>;
        }

        return (
            <Menu.Item className={"geo-component geo-widget geo-search"}>
                {searchField}
            </Menu.Item>
        );
    }

    loadSearch(): Promise<any> {
        const {config, store} = this.props;
        const {layersLoaded} = this.state;

        return new Promise((resolve, reject) => {
            try {
                // rerun until map view is ready and search view model is loaded
                this.autorunDisposers.push(autorun((reaction: any) => {
                    if (!store?.mapStore.view ||
                        (config?.waitForTocReady && !store?.mapStore.tocReady)) {
                        return;
                    }

                    const sourcesWithLayers = config.sources.filter((s: SearchSourceConfiguration) =>
                        s.sourceType === SearchSourceType.Layer ||
                            (s.sourceType === SearchSourceType.Custom && (s as any).layerId));
                    if (sourcesWithLayers.length) {
                        // wait for layers to load
                        const allLayersLoaded = sourcesWithLayers.every((s: SearchSourceConfiguration) =>
                            store?.mapStore.layers.get((s as any).layerId));
                        if (!allLayersLoaded) {
                            return;
                        }
                    }
                    const searchViewModel =
                        this.loadViewModel(config, store, !!store?.mapStore.tocReady,
                            layersLoaded);
                    if (searchViewModel) {
                        reaction.dispose(); // stop autorun
                        resolve({ viewModel: searchViewModel });
                    }
                }));
            } catch (err) {
                reject(err);
            }
        });
    }

    loadViewModel(searchConfig: SearchConfiguration,
                  store: IAppStore,
                  tocReady: boolean,
                  layersLoaded: number): SearchViewModel | null {
        if (!store || (!tocReady && searchConfig?.waitForTocReady)) {
            return null;
        }

        const sources = this.initializeSources(store);
        if (!sources || layersLoaded < 0) {
            return null;
        }
        const config = searchConfig as __esri.widgetsSearchProperties;
        const props = {
            view: store.mapStore.view,
            sources,
            container: "search-input",
            ...config
        };
        return new SearchViewModel(props);
    }

    /**
     * Initialize search sources
     */
    initializeSources(store?: IAppStore):
    __esri.Collection<__esri.LayerSearchSource | __esri.LocatorSearchSource | __esri.SearchSource> | null {
        if (!this.config.sources || !store) {
            return new EsriCollection();
        }

        if (!Array.isArray(this.config.sources)) {
            throw new Error("Error in search configuration. 'sources' is not an array.");
        }

        const sources = new EsriCollection();
        let sourceIndex = 0;
        this.popupEnabled.length = 0;
        this.autoNavigate.length = 0;
        for (const source of this.config.sources) {
            if (source.popupEnabled) {
                // suppress Esri Popup
                this.popupEnabled.push(sourceIndex);
                source.popupEnabled = false;
            }
            if (!source.hasOwnProperty("autoNavigate") || source.autoNavigate) {
                this.autoNavigate.push(sourceIndex);
                // Falls autoNavigate auf "true" gesetzt ist, wird diese erst nach der Geometrie-Rückgabe ausgelöst.
                if (source.autoNavigate) {
                    source.autoNavigate = false;
                }
            }
            source.maxSuggestions = (typeof source.maxSuggestions === "number")? source.maxSuggestions + 1: 9;
            if (this.config.resultSymbol?.pointer) {
                source.resultSymbol = this.config.resultSymbol.pointer;
            }
            if (source.sourceType === SearchSourceType.Locator) {
                sources.add(source);
            } else if (source.sourceType === SearchSourceType.Layer) {
                const layerSource = this.initializeLayerSource(source as LayerSearchSourceConfiguration, store);
                if (layerSource) {
                    sources.add(layerSource);
                }
            }else if (source.sourceType === SearchSourceType.Custom) {
                const customSource = this.initializeCustomSource(source as CustomSearchSourceConfiguration, store);
                if (customSource) {
                    sources.add(customSource);
                }
            }else if (source.sourceType === SearchSourceType.Coordinate) {
                const customSource = this.initializeCoordinateSource(source as CoordinateSearchSourceConfiguration);
                if (customSource) {
                    sources.add(customSource);
                }
            }
            sourceIndex++;
        }
        return sources;
    }
    componentDidMount(): void {
        const {onLoad, store} = this.props;
        if (store?.uiStore.widgets.has(this.widgetKey)) {
            return;
        }
        store?.SearchStore.setViewReady(this.widgetKey, false);
        this.loadSearch().then((result: any) => {
            const viewModel = result.viewModel;
            this.setState({
                isLoading: false,
                placeholder: viewModel.placeholder,
                viewModel
            });
            runInAction(()=> {
                store?.uiStore.addWidget(this.widgetKey, this);
                store?.SearchStore.setViewReady(this.widgetKey, true);
                store?.SearchStore.setViewModel(this.widgetKey, viewModel);
            });
            this.autorunDisposers.push(autorun(
                () => {
                    if ( !store) {
                        return;
                    }
                    this.config = _.cloneDeep(store?.appConfig.search) as SearchConfiguration;
                    viewModel.sources = this.initializeSources(store);
                    this.setState({
                        placeholder: store?.appConfig.search.allPlaceholder,
                        viewModel
                    });
                }
            ));
            if (onLoad) {
                // call the hook
                onLoad(viewModel);
            }
        });
    }
    // componentDidUpdate() {
    //     // sync the observable from props
    //     this.mapStore = this.props.store?.mapStore;
    // }
    componentWillUnmount(): void {
        const {store} = this.props;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
        store?.SearchStore.setViewReady(this.widgetKey, false);
    }

    /**
     * Pass suggestions from autosuggest to search view model
     *
     * @param value
     */
    getSuggestions(value: string): void {
        const {viewModel, sectionShowAll} = this.state;
        if (!viewModel) {
            // search not fully loaded yet
            return;
        }
        const inputValue = value.trim().toLowerCase();
        const inputLength = inputValue.length;

        if (inputLength === 0) {
            return;
        }
        viewModel.suggest(inputValue).then((response: __esri.SuggestResponse) => {
            // TODO auf autosuggest sections verteilen
            // Siehe https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchViewModel.html#SearchResponse
            // TODO: eventuell muss suggestions noch gefiltert werden, wenn asynchrone resultate eintreffen, damit
            // nur die treffer mit mind. der Länge des eingegegbenen Worts angezeigt werden
            // z.B. .filter((sug:any) => sug.text.toLowerCase().slice(0, inputLength) === inputValue )
            // TODO: response.errors auswerten und darstellen
            let sections = response.results.map((result: __esri.SuggestResponseResults) => ({
                title: result.source.name,
                numResults: result.results.length,
                suggestions: result.results,
                sourceIndex: result.sourceIndex
            }));
            // leere Seketionen rausfiltern
            sections = sections.filter((section: any) => section.numResults > 0);
            this.suggestions = _.cloneDeep(sections); // clone aller Resultate
            // limitieren der dargestellten Resultate
            sections = sections.map((section: any) => {
                const sourceConfig = this.config.sources[section.sourceIndex];
                if (!sectionShowAll[section.sourceIndex]
                    && sourceConfig.maxResults
                    && section.suggestions.length > sourceConfig.maxResults) {
                    section.suggestions.length = sourceConfig.maxResults;
                }
                return section;
            });
            this.setState({
                searchTerm: response.searchTerm,
                suggestions: sections
            });
        });
    }

    private initializeLayerSource(source: LayerSearchSourceConfiguration, store: IAppStore) {
        if (!source || (!source.layerId && !source.metauid && !source.layer)) {
            return null;
        }
        let layer: any;
        if(source.layerId && !source.metauid) {
            layer = store.mapStore?.layers.get(source.layerId) as __esri.FeatureLayer;
            if(!layer) {
                layer = store.mapStore?.groupLayers.get(source.layerId) as __esri.FeatureLayer;
            }
        }
        if (!layer && source.metauid && store.mapStore?.map?.allLayers) {
            layer = store.mapStore?.map?.allLayers.flatten((subL: __esri.Layer) =>
                (subL as __esri.MapImageLayer)?.allSublayers as unknown as __esri.Collection<__esri.Layer>)
                .find((l: __esri.Layer) => {
                    let metaId = (l as any).metauid;
                    let layerId: number | null = null;
                    if (!metaId){
                        const url = PathUtils.getLayerPath(l);
                        if (typeof url === "string") {
                            metaId = PathUtils.getMetaId(url);
                            layerId = PathUtils.getLayerId(url);
                        }
                    }
                    if (metaId) {
                        if(source.metauid === metaId &&
                            (!source.hasOwnProperty("layerId") || layerId === parseInt(source.layerId, 10))) {
                            return true;
                        }
                    }
                    return false;
                });
        }
        if (!layer && source.layer) {
            layer = new FeatureLayer(source.layer as __esri.FeatureLayerProperties);
        }
        if (layer) {
            source.layer = layer as FeatureLayer;
            if (((layer.sublayers || source.returnDistinctValues || source.exactMatch === false)
                && source.searchFields) || source.customQuery) {
                if(typeof source.searchLayers === "undefined"){
                    source.searchLayers = [];
                }
                if (layer.children) {
                    // Sicherstellen das der Layer geladen wurde
                    layer.load().then(()=>{
                        const mapImageLayer = layer.parent as __esri.MapImageLayer;
                        layer.children.forEach((layerId: number)=> {
                            // Sublayer laden, damit "fields" vorhanden ist
                            mapImageLayer.findSublayerById(layerId).load()
                                .then((fl) => {
                                    if(typeof source.searchLayers === "undefined"){
                                        source.searchLayers = [];
                                    }
                                    source.searchLayers.push(fl);
                                });

                        });
                    });

                } else if (layer.sublayers) {
                    source.searchLayers = layer.sublayers.toArray();
                } else if (layer.type === "group") {
                    source.searchLayers = layer.layers.toArray();
                } else {
                    source.searchLayers.push(layer as FeatureLayer);
                }
                source.getSuggestions = async (params) => {
                    const queryPromises: Array<Promise<__esri.FeatureSet | undefined>> = [];
                    const resultFeatures: featureResult[] = [];
                    if(typeof source.searchLayers === "undefined"){
                        source.searchLayers = [];
                    }
                    const profile: __esri.Profile = {
                        variables: [{
                            name: "$feature",
                            type: "feature"
                        }]
                    };
                    source.searchLayers.forEach((sLayer, index)=>{
                        queryPromises.push(new Promise((resolve) => {
                            if(sLayer) {
                                const query = (sLayer as FeatureLayer).createQuery();
                                const whereArray: string[] = [];
                                if (source.customQuery) {
                                    if (source.customNumericQuery && !isNaN(params.suggestTerm)) {
                                        const num = parseInt(params.suggestTerm, 10);
                                        whereArray.push(source.customNumericQuery.replaceAll(
                                            "{}", num.toString()));
                                    } else {
                                        whereArray.push(source.customQuery.replaceAll(
                                            "{}",FormatUtils.encodeForSQL(params.suggestTerm.toUpperCase())));
                                    }
                                } else {
                                    source.searchFields?.forEach((fieldName) => {
                                        const field = (sLayer as FeatureLayer).getField(fieldName);
                                        if (field) {
                                            if (fieldName === "OBJECTID"
                                                || field.type === "integer" || field.type === "double"
                                                || field.type === "small-integer") {
                                                if (!isNaN(params.suggestTerm)) {
                                                    // Nummer startet mit Sucheingabe, falls exactMatch definiert und
                                                    // auf false
                                                    if (source.exactMatch === false) {
                                                        whereArray.push("CAST(" + fieldName + "  AS VARCHAR(20)) LIKE '"
                                                            + parseInt(params.suggestTerm, 10) + "%'");
                                                    } else {
                                                        whereArray.push(fieldName + " = "
                                                            + parseInt(params.suggestTerm, 10));
                                                    }
                                                }
                                            } else {
                                                let wClase = "UPPER(" + fieldName + ") LIKE '%"
                                                    + FormatUtils.encodeForSQLLike(params.suggestTerm.toUpperCase())
                                                    + "%'";
                                                if (wClase.indexOf("\\") > 0) {
                                                    wClase += " ESCAPE '\\'";
                                                }
                                                whereArray.push(wClase);
                                            }
                                        }
                                    });
                                }
                                if (whereArray.length === 0) {
                                    resolve(new FeatureSet());
                                    return;
                                }
                                if (source.filter?.where) {
                                    query.where = "(" + whereArray.join(" OR ")+") AND ("+source.filter.where+")";
                                } else {
                                    query.where = whereArray.join(" OR ");
                                }
                                if (source.orderByFields) {
                                    query.orderByFields = source.orderByFields;
                                }
                                query.outFields = (source.outFields?.length)? source.outFields: ["*"];
                                query.returnDistinctValues = !!source.returnDistinctValues;
                                query.num = source.maxSuggestions? source.maxSuggestions: 99;
                                query.returnGeometry = false;
                                sLayer.queryFeatures(query).then((response: __esri.FeatureSet) => {
                                    if (response.features.length > 0) {
                                        response.features.forEach((feature)=>{
                                            resultFeatures.push({feature, sourceIndex: index});
                                        });
                                    }
                                    resolve(response);
                                });
                            }
                        }));
                    });
                    return Promise.all(queryPromises).then(async () => {
                        if (resultFeatures.length === 0) {
                            return [];
                        }
                        if (!this.suggestionKeyMap.has(params.sourceIndex)) {
                            this.suggestionKeyMap.set(params.sourceIndex, new Map());
                        } else {
                            this.suggestionKeyMap.get(params.sourceIndex)?.clear();
                        }
                        let features: any = [];
                        resultFeatures.forEach((r)=>{
                            features.push({...r.feature.attributes, feature: r.feature, layerIndex: r.sourceIndex});
                        });
                        if (source.orderByFields) {
                            const orderby: string[] = [];
                            const orders: Array<"asc" | "desc"> = [];
                            source.orderByFields.forEach((o) => {
                                // nach Sortierrichtung suchen
                                const arr = o.split(" ");
                                orderby.push(arr[0]);
                                orders.push((arr[1] && arr[1].toLowerCase()==="desc")? "desc": "asc");
                            });
                            features = _.orderBy(features, orderby, orders);
                        }
                        features = features.slice(0,source.maxSuggestions? source.maxSuggestions: 99);
                        return await Promise.all(features.map(async (resultFeature: any) => {
                            const feature = resultFeature.feature;
                            let suggestionPromise: Promise<string>;
                            let text = "";
                            if (source.suggestionTemplate) {
                                if(source.suggestionTemplate?.indexOf("`")>-1){
                                    suggestionPromise = new Promise((resolve) => {
                                        const arcadeExecutor = arcade.createArcadeExecutor(
                                            source.suggestionTemplate || "", profile);
                                        arcadeExecutor.then((ae) => {
                                            text = ae.execute({
                                                $feature: feature
                                            });
                                            resolve(text? text: "");
                                        });
                                    });
                                }else{
                                    const outFields = (source.outFields?.length)? source.outFields: ["*"];
                                    text = source.suggestionTemplate;
                                    outFields.forEach((fieldName: string) => {
                                        const fieldVal = (feature.attributes[fieldName] === null)?
                                            "": feature.attributes[fieldName];
                                        text = text.replace("{" + fieldName + "}", fieldVal);
                                    });
                                    suggestionPromise = new Promise((resolve) => {
                                        resolve(text);
                                    });
                                }
                            } else if (source.displayField) {
                                text += feature.attributes[source.displayField];
                                suggestionPromise = new Promise((resolve) => {
                                    resolve(text);
                                });
                            }else{
                                suggestionPromise = new Promise((resolve) => {
                                    resolve(text);
                                });
                            }
                            const keyArray: string[] = [];
                            source.outFields?.forEach((fieldName) => {
                                const searchLayer = source.searchLayers?
                                    (source.searchLayers[resultFeature.layerIndex] as FeatureLayer): null;
                                const field = (searchLayer as FeatureLayer).getField(fieldName);
                                if (feature.attributes[fieldName] !== undefined) {
                                    if (feature.attributes[fieldName] === null) {
                                        keyArray.push("" + fieldName + " IS NULL");
                                    } else if (field && (fieldName==="OBJECTID"
                                        || field.type === "integer" || field.type === "double"
                                        || field.type === "small-integer")) {
                                        if (!isNaN(feature.attributes[fieldName])) {
                                            keyArray.push(fieldName + " = "
                                                + parseInt(feature.attributes[fieldName], 10));
                                        }
                                    } else {
                                        const wClase = "" + fieldName + " = '"
                                            + FormatUtils.encodeForSQL(feature.attributes[fieldName]) + "'";
                                        keyArray.push(wClase);
                                    }
                                }
                            });
                            const key = Math.random().toString(36).substring(2); // Einfacher Zufallsschlüssel
                            this.suggestionKeyMap.get(params.sourceIndex)?.set(key, [
                                resultFeature.layerIndex.toString(),
                                keyArray.join(" AND ")]);
                            return await suggestionPromise.then((t)=>({
                                text: t,
                                key,
                                sourceIndex: params.sourceIndex
                            }));
                        }));
                    });
                };
                source.getResults = (params) => {
                    const where = this.suggestionKeyMap.get(params.sourceIndex)?.get(params.suggestResult.key);
                    if (!where) {
                        // eslint-disable-next-line no-console
                        console.warn("Key nicht gefunden");
                        return new Promise((resolve) =>  resolve([]));
                    }
                    const searchLayer = source.searchLayers?
                        (source.searchLayers[parseInt(where[0], 10)] as FeatureLayer): null;
                    if(!searchLayer){
                        // eslint-disable-next-line no-console
                        console.warn("Layer in getResults nicht gefunden");
                        return new Promise((resolve) =>  resolve([]));
                    }
                    const query = searchLayer.createQuery();
                    query.where = where[1];

                    if (source.orderByFields) {
                        query.orderByFields = source.orderByFields;
                    }
                    query.outFields = (source.outFields?.length)? source.outFields: ["*"];
                    query.num = source.maxResults? source.maxResults: 99;
                    return searchLayer.queryFeatures(query).then((response) =>
                        // Return results
                        response.features.map((feature) => {
                            let text = "";
                            if (source.suggestionTemplate) {
                                text = source.suggestionTemplate;
                                query.outFields.forEach((fieldName: string) => {
                                    const fieldVal = (feature.attributes[fieldName] === null)?
                                        "": feature.attributes[fieldName];
                                    text = text.replace("{" + fieldName + "}", fieldVal);
                                });
                            } else if (source.displayField) {
                                text += feature.attributes[source.displayField];
                            }
                            let extent = feature.geometry.extent;
                            // Extent mit mindestens 100x100 Meter sicherstellen
                            if (!extent) {
                                const buffer = geometryEngine.buffer(feature.geometry, 50, "meters");
                                if(Array.isArray(buffer)) {
                                    extent = buffer[0].extent;
                                } else if (buffer.extent) {
                                    extent = buffer.extent;
                                }
                            }else if(extent.width < 100 || extent.height < 100) {
                                if(extent.width < 100){
                                    const xPadding = Math.ceil((100 - extent.width)/2);
                                    extent.xmin-=xPadding;
                                    extent.xmax+=xPadding;
                                }
                                if(extent.height < 100){
                                    const yPadding = Math.ceil((100 - extent.height)/2);
                                    extent.ymin-=yPadding;
                                    extent.ymax+=yPadding;
                                }
                            }
                            return {
                                extent,
                                feature,
                                name: text,
                                sourceIndex: params.sourceIndex,
                                target: feature
                            };
                        })
                    );
                };
            }
            return source;
        } else {
            // eslint-disable-next-line no-console
            console.warn("Layer not found for LayerSearchSource");
            return null;
        }
    }

    private initializeCustomSource(source: CustomSearchSourceConfiguration, store: IAppStore) {
        if (!source || !source.layerId) {
            return null;
        }
        const layer = store.mapStore?.layers.get(source.layerId) as __esri.FeatureLayer;
        if (!layer) {
            // eslint-disable-next-line no-console
            console.warn("Layer not found for CustomSearchSource");
        }
        source.layer = layer;

        // TODO define the class to use for custom search in configuration file
        const stationSearch = new SearchStation(source);
        return new SearchSource(stationSearch);
    }
    private initializeCoordinateSource(source: CoordinateSearchSourceConfiguration) {
        source.getSuggestions = (params) => new Promise((resolve, reject) => {
            try {
                let coordString = params.suggestTerm;
                coordString = coordString.replaceAll("/", " ");
                coordString = coordString.replaceAll(",", " ");
                coordString = coordString.replaceAll(/[a-z]/ig, "");
                coordString = coordString.replaceAll(/[ ]+/g, " ");
                coordString = coordString.trim();
                const coord: number[] = [];
                if(params.suggestTerm.indexOf("°")>=0){
                    const parts = coordString.split(/[^\d .]+/);
                    if(parts.length >= 6) {
                        coord[0]=this.convertDMSToDD(parts[0], parts[1], parts[2]);
                        coord[1]=this.convertDMSToDD(parts[3], parts[4], parts[5]);
                    }
                }else{
                    coordString = coordString.replaceAll(/[^0-9. ]/ig,"");
                    coordString = coordString.trim();
                    const parts = coordString.split(" ");
                    coord[0]=parseFloat(parts[0]);
                    coord[1]=parseFloat(parts[1]);
                }
                if(coord.length === 2) {
                    let graphic: __esri.Graphic | undefined;
                    if(coord[0] < 100){ // wgs84
                        coord.sort((a: number, b: number) => a - b);
                        if(this.checkExtent(coord[0], coord[1], source.maxExtent?.wgs84)) {
                            graphic = new EsriGraphic({
                                geometry: {
                                    spatialReference: {wkid: 4326},
                                    type: "point",
                                    longitude: coord[0], latitude: coord[1]
                                } as PointConfiguration
                            });
                        }
                    }else if(coord[0] < 1000000){ // lv03
                        coord.sort((a: number, b: number) => b - a);
                        if(this.checkExtent(coord[0], coord[1], source.maxExtent?.lv03)){
                            graphic = new EsriGraphic({
                                geometry: {
                                    spatialReference: { wkid: 21781 },
                                    type: "point",
                                    x: coord[0], y: coord[1]} as PointConfiguration
                            });
                        }
                    }else{ // lv95
                        coord.sort((a: number, b: number) => b - a);
                        if(this.checkExtent(coord[0], coord[1], source.maxExtent?.lv95)){
                            resolve([{
                                text: new Intl.NumberFormat("de-CH").format(Math.round(coord[0]))
                                        + " / "+ new Intl.NumberFormat("de-CH").format(Math.round(coord[1])),
                                key: coord[0]+ "_"+ coord[1],
                                sourceIndex: params.sourceIndex}]);
                        }
                    }
                    if(graphic){
                        projection.load().then(()=> {
                            if(graphic) {
                                const projectedPoint = projection.project(graphic.geometry, {wkid: 2056});
                                let point: __esri.Point;
                                if (Array.isArray(projectedPoint)) {
                                    point = projectedPoint[0] as __esri.Point;
                                } else {
                                    point = projectedPoint as __esri.Point;
                                }
                                coord[0] = point.x;
                                coord[1] = point.y;
                                resolve([{
                                    text: new Intl.NumberFormat("de-CH").format(Math.round(coord[0]))
                                            + " / " + new Intl.NumberFormat("de-CH").format(Math.round(coord[1])),
                                    key: coord[0] + "_" + coord[1],
                                    sourceIndex: params.sourceIndex
                                }]);
                            }
                        });
                    } else {
                        resolve([]);
                    }

                } else {
                    resolve([]);
                }
            } catch (err) {
                reject(err);
            }
        });
        source.getResults = (params) => new Promise((resolve) => {
            const coord = params.suggestResult.key.split("_");
            const x = parseInt(coord[0], 10);
            const y= parseInt(coord[1], 10);
            const graphic = new EsriGraphic({
                geometry: {
                    spatialReference: { wkid: 2056 },
                    type: "point",
                    x, y} as PointConfiguration
            });
            resolve([{
                extent: new Extent({
                    spatialReference: {wkid: 2056},
                    xmax: x+100,
                    xmin: x-100,
                    ymax: y+100,
                    ymin: y-100
                }),
                feature: graphic,
                name: params.suggestResult.text,
                // @ts-expect-error Bug in SDK 4.29
                sourceIndex: params.sourceIndex, // TODO: wird sourceIndex noch benötigt? Bug in SDK 4.29.?
                target: graphic
            }]);

        });
        return source;
    }
    // https://stackoverflow.com/questions/1140189/converting-latitude-and-longitude-to-decimal-values
    private convertDMSToDD(degrees: string, minutes: string, seconds: string): number {
        return parseFloat(degrees) + parseFloat(minutes)/60 + parseFloat(seconds)/(60*60);
    }
    private checkExtent(x: number, y: number, maxExtent?: ExtentConfiguration): boolean {
        if(!maxExtent) { return true; }
        if(!maxExtent.xmax || !maxExtent.xmin || !maxExtent.ymax || !maxExtent.ymin) { return false; }
        return x > maxExtent.xmin && x < maxExtent.xmax && y > maxExtent.ymin && y < maxExtent.ymax;
    }
}

export const Search = withStore(SearchBase);
